import { CCTV } from '@/interfaces/CCTV';
import Config from '@/interfaces/Config';
import LocalConfig from '@/interfaces/LocalConfig';
import RequestAndroidOption from '@/interfaces/RequestAndroidOption';
import ResponseServerConfig from '@/interfaces/ResponseServerConfig';
import { logger } from '@/logger';
import { useModalStore } from '@/stores/modal';
import { definedProps } from '@/utils';
import { useStorage } from '@vueuse/core';
import dayjs from 'dayjs';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const useConfigStore = defineStore('config', () => {
  const modalStore = useModalStore();

  // 로컬 설정
  const localConfig = useStorage('CONFIG', {} as LocalConfig);
  // 서버 설정
  const serverConfig = ref<ResponseServerConfig>();
  // 모바일 메뉴 접속 여부
  const isMobileMenu = ref(false);
  // 안드로이드 버전
  const androidVersion = ref('0');
  // CCTV NVR 주소
  const nvrAddress = ref<string>();
  // CCTV NVR 채널 수
  const nvrChannelCount = ref<number>();

  // 서버설정과 로컬설정을 합한 최종 설정 값
  const config = computed((): Config => {
    return Object.assign({} as Config, serverConfig.value, definedProps(localConfig.value));
  });

  // 모바일 모드로 설정했거나 또는 모바일 메뉴로 접속한 경우 모바일 레이아웃 사용
  const isMobileLayout = computed((): boolean => {
    return isMobileMenu.value || config.value.IsMobileMode || false;
  });

  // 결제 사용 여부
  const usePayment = computed((): boolean => {
    if (config.value.IsOrderOnlyUsed) return false;
    if (config.value.IsPaymentModeUsed) return true;
    return false;
  });

  // 선주문후결제 사용 여부
  const usePreOrder = computed((): boolean => {
    return config.value.IsPreOrderUsed && usePayment.value;
  });

  // 1인 1메뉴 옵션 사용여부 (인원수 입력 사용여부 체크)
  const useOneMenuPerPerson = computed((): boolean => {
    return config.value.IsOneMenuPerPersonUsed && (config.value.IsHeadcountInputUsed || false);
  });

  /** 전자 영수증 사용 여부 */
  const useElectronicReceipt = computed(
    () => config.value.IsElectronicReceiptUsed && !config.value.IsPrepaidOrderKeepingUsed,
  );

  /** 영수증 사용 여부 */
  const useReceipt = computed(() => useElectronicReceipt.value || config.value.IsPaperReceiptUsed);

  /**
   * 로컬 설정 세팅
   * @param _localConfig 로컬 설정 값
   */
  const setLocalConfig = (_localConfig: LocalConfig) => {
    Object.entries(_localConfig).forEach(([key, value]) => {
      if (typeof value !== 'boolean' && !value) delete _localConfig[key as keyof typeof _localConfig];
    });

    localConfig.value = _localConfig;
  };

  /**
   * 서버 설정 세팅
   * @serverConfigParam serverConfigParam 서버 설정 값
   */
  const setServerConfig = (_serverConfig: ResponseServerConfig) => {
    serverConfig.value = _serverConfig;

    // 서버 설정에서 CCTV 정보가 있다면 현재 태블릿의 NVR 주소를 설정
    if (config.value.CCTV) {
      try {
        const cctv = JSON.parse(_serverConfig.CCTV) as CCTV;

        if (cctv.IsUsed && cctv.NvrList && cctv.NvrList.length > 0) {
          // 현재 태블릿에 해당하는 NVR ID를 찾는다.
          const tabletNvrId = cctv.TabletList.find((tablet) => tablet.PosId === localConfig.value.PosCode)?.NvrId;
          // 현재 태블릿에 해당하는 NVR을 찾는다.
          const nvr = cctv.NvrList.find((nvr) => nvr.Id === tabletNvrId) ?? cctv.NvrList[0];
          // NVR이 없으면 첫번째 NVR 주소 사용
          nvrAddress.value = nvr.Address;
          // NVR 채널 수가 있다면 해당 값을 사용
          nvrChannelCount.value = nvr.ChannelCount;
        }
      } catch (error) {
        logger.error('[ConfigStore] CCTV JSON 파싱 오류:', _serverConfig.CCTV, error);
      }
    }
  };

  /**
   * 모바일 메뉴 접속 여부 세팅
   * @isMobileMenuParam isMobileMenuParam 모바일 메뉴 접속 여부
   */
  const setIsMobileMenu = (isMobileMenuParam: boolean) => {
    isMobileMenu.value = isMobileMenuParam;
  };

  // UP오더 설정 안드로이드 옵션 조회
  const getAndroidOption = (): RequestAndroidOption => {
    const request: RequestAndroidOption = {
      PosID: config.value.PosCode || undefined,
      IsAutoStart: true,
      VANCode: config.value.VanCode === 0 ? '' : config.value.VanCode.toString(),
      UseServerPOSApiURL: localConfig.value.POS_API_URL ? false : true,
      OrderType: config.value.IsPaymentModeUsed ? '1' : '0',
      UseServerOrderType: localConfig.value.IsPaymentModeUsed === undefined ? true : false,
      RotateMode: config.value.RotateMode ?? '0',
      UseServerRotateMode: localConfig.value.RotateMode ? false : true,
      UseWidgetMode: config.value.IsWidgetModeUsed ?? false,
      UseServerWidgetMode: localConfig.value.IsWidgetModeUsed === undefined ? true : false,
    };
    return request;
  };

  /**
   * 현금 결제 사용 여부 조회
   * @returns 카운터 결제 사용 여부
   */
  const getIsCashPaymentUsed = (): boolean => {
    if (!config.value.IsCashPaymentUsed) return false;

    if (
      config.value.IsCashPaymentUsed &&
      config.value.CashPaymentOffTimes &&
      config.value.CashPaymentOffTimes.length > 0
    ) {
      const now = Number(dayjs().format('HHmm'));
      return !config.value.CashPaymentOffTimes.some((time) => {
        if (!time.IsUsed) return false;
        if (!time.StartTime || !time.EndTime) return false;
        const startTime = Number(time.StartTime.replace(':', ''));
        const endTime = time.EndTime === '00:00' ? 2400 : Number(time.EndTime.replace(':', ''));
        return now >= startTime && now <= endTime;
      });
    }

    return true;
  };

  const setAndroidVersion = (version: string) => {
    androidVersion.value = version;
  };

  return {
    localConfig,
    serverConfig,
    isMobileMenu,
    config,
    isMobileLayout,
    usePayment,
    usePreOrder,
    useOneMenuPerPerson,
    useElectronicReceipt,
    useReceipt,
    androidVersion,
    nvrAddress,
    nvrChannelCount,
    setLocalConfig,
    setServerConfig,
    setIsMobileMenu,
    getAndroidOption,
    getIsCashPaymentUsed,
    setAndroidVersion,
  };
});
